export function toggleModule(selected, name, direction) {
  const on = selected.includes(name);
  let next = on ? selected.filter((item) => item !== name) : [...selected, name];
  if ((name === "contact" || name === "case") && !on && !next.includes("customer")) {
    next = ["customer", ...next];
  }
  if (name === "customer" && on) {
    next = next.filter((item) => item !== "contact" && item !== "case");
  }
  if (direction === "outbound") {
    next = next.filter((item) => item !== "case");
  }
  return next;
}

export function patchHookInList(hooks, hookId, fields) {
  return hooks.map((hook) => (hook.id === hookId ? { ...hook, ...fields } : hook));
}

export function applyModuleToggle(hooks, hookId, moduleName) {
  const hook = hooks.find((item) => item.id === hookId);
  if (!hook) return { hooks, modules: null, rejected: "missing" };
  const modules = toggleModule(hook.modules || [], moduleName, hook.direction);
  if (!modules.length) return { hooks, modules, rejected: "blank" };
  return { hooks: patchHookInList(hooks, hookId, { modules }), modules, rejected: null };
}

export const PRESERVE_FIELDS = ["modules", "label_ids", "status"];

export function applyLabelIds(hooks, hookId, labelIds) {
  const hook = hooks.find((item) => item.id === hookId);
  if (!hook) return { hooks, label_ids: null, rejected: "missing" };
  const label_ids = Array.isArray(labelIds) ? labelIds : [];
  return { hooks: patchHookInList(hooks, hookId, { label_ids }), label_ids, rejected: null };
}

export function applyStatus(hooks, hookId, status) {
  const hook = hooks.find((item) => item.id === hookId);
  if (!hook) return { hooks, status: null, rejected: "missing" };
  return { hooks: patchHookInList(hooks, hookId, { status }), status, rejected: null };
}

export function applyCreatedHook(hooks, hook) {
  if (!hook || hook.id == null) return hooks;
  const safe = { ...hook };
  delete safe.secret;
  if (hooks.some((item) => item.id === safe.id)) {
    return patchHookInList(hooks, safe.id, safe);
  }
  return [...hooks, safe];
}

export function removeHookFromList(hooks, hookId) {
  return hooks.filter((item) => item.id !== hookId);
}

export function unionDirtyFields(...maps) {
  const out = new Map();
  maps.forEach((map) => {
    if (!map) return;
    const entries = map instanceof Map ? map : new Map(Object.entries(map));
    entries.forEach((flags, id) => {
      const key = typeof id === "string" && /^\d+$/.test(id) ? Number(id) : id;
      out.set(key, { ...(out.get(key) || {}), ...flags });
    });
  });
  return out;
}

export function mergeOmittedIds(...sets) {
  const out = new Set();
  sets.forEach((set) => {
    (set || []).forEach((id) => out.add(id));
  });
  return out;
}

export function mergeRemoteHooks(remoteHooks, localHooks, dirtyById, omittedIds) {
  const omit = omittedIds instanceof Set ? omittedIds : new Set(omittedIds || []);
  const localById = new Map((localHooks || []).map((hook) => [hook.id, hook]));
  const merged = (remoteHooks || [])
    .filter((remote) => !omit.has(remote.id))
    .map((remote) => {
      const dirty = dirtyById instanceof Map ? dirtyById.get(remote.id) : dirtyById?.[remote.id];
      const local = localById.get(remote.id);
      if (!dirty || !local) return remote;
      const overlay = {};
      PRESERVE_FIELDS.forEach((field) => {
        if (dirty[field]) overlay[field] = local[field];
      });
      return { ...remote, ...overlay };
    });
  const remoteIds = new Set(merged.map((hook) => hook.id));
  const extras = (localHooks || []).filter((hook) => !remoteIds.has(hook.id) && !omit.has(hook.id));
  return extras.length ? [...merged, ...extras] : merged;
}

export function createWebhookPatchQueue({ update, onError }) {
  const pending = new Map();
  const inflight = new Set();
  const inflightPayloads = new Map();
  const tails = new Map();
  const discarded = new Set();

  const markDirty = (out, id, payload) => {
    if (!payload) return;
    const cur = out.get(id) || {};
    PRESERVE_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(payload, field)) cur[field] = true;
    });
    if (Object.keys(cur).length) out.set(id, cur);
  };

  const dirtyFields = () => {
    const out = new Map();
    pending.forEach((payload, id) => markDirty(out, id, payload));
    inflightPayloads.forEach((payload, id) => markDirty(out, id, payload));
    return out;
  };

  const settle = (id) => {
    inflight.delete(id);
    inflightPayloads.delete(id);
  };

  const flush = (id) => {
    const payload = pending.get(id);
    if (!payload) return tails.get(id) || Promise.resolve();
    if (inflight.has(id)) return tails.get(id) || Promise.resolve();

    pending.delete(id);
    inflight.add(id);
    inflightPayloads.set(id, payload);

    let started;
    try {
      started = update(id, payload);
    } catch (err) {
      settle(id);
      pending.delete(id);
      if (!discarded.has(id)) onError?.(err, id);
      const failed = Promise.resolve();
      tails.set(id, failed);
      return failed;
    }

    const run = Promise.resolve(started)
      .then(() => {
        settle(id);
        if (discarded.has(id)) return;
        if (pending.has(id)) return flush(id);
      })
      .catch((err) => {
        settle(id);
        pending.delete(id);
        if (!discarded.has(id)) onError?.(err, id);
      });

    tails.set(id, run);
    return run;
  };

  return {
    enqueue(id, payload) {
      pending.set(id, { ...(pending.get(id) || {}), ...payload });
      return flush(id);
    },
    dirtyFields,
    discardedIds() {
      return new Set(discarded);
    },
    drop(id) {
      pending.delete(id);
      discarded.add(id);
    },
  };
}
